import { useAppDispatch } from "app/hooks";
import { globalMessageHandler, setThemeMode } from "common";
import {
  loadSharingByToken,
  SharingItem,
  submitSharingWaiver,
} from "features/sharings/sharingApi";
import I18n from "i18n-js";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldType } from "../../../components/FormEditor/types";
import { WaiverFormItem } from "../waiverApi";

export type WaiverSubmitState = {
  token?: string;
  waiver_id?: string;
  sharing?: SharingItem;
  setSharing: (sharing?: SharingItem) => void;
  item?: WaiverFormItem;
  previewItem?: WaiverFormItem;
  setItem: (item?: WaiverFormItem) => void;
  hasMoreWaivers: boolean;
  pages: any[];
  setPages: (pages: any[]) => void;
  handleLoadSuccess: (data: any) => void;
  handleLoadError: (error: any) => void;
  submit: () => Promise<void>;
  errors: any;
  setErrors: (errors: any) => void;
  showSuccessModal: boolean;
  setShowSuccessModal: (show: boolean) => void;
  handleFillAnother: () => void;
  formKey: string;
  loading: boolean;
  submitting: boolean;
};

export interface UseWaiverSubmitProps {
  previewItem?: WaiverFormItem;
}

export default function useWaiverSubmit({
  previewItem,
}: UseWaiverSubmitProps): WaiverSubmitState {
  const dispatch = useAppDispatch();
  const { token, waiver_id } = useParams<"token" | "waiver_id">();
  const [sharing, setSharing] = useState<SharingItem>();
  const [errors, setErrors] = useState<any>();
  const [item, setItem] = useState<WaiverFormItem | undefined>(previewItem);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formKey, setFormKey] = useState(() => `form-${Date.now()}`);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasMoreWaivers =
    sharing?.waivers && sharing?.waivers?.length > 1 ? true : false;
  const [pages, setPages] = useState<any[]>([]);
  const navigate = useNavigate();

  function handleLoadSuccess({ numPages, ...rest }: any) {
    //setLoading(false);
    setPages([...new Array(numPages).fill(null)]);
  }

  function handleLoadError(error: any) {
    //setLoading(false);
    setPages([]);
  }

  async function loadSharing() {
    setLoading(true);
    try {
      const { success, data, error } = await loadSharingByToken(token);
      if (success) {
        if (data.data?.waivers?.length === 1) {
          //setItem(data.data?.waivers[0]);
          navigate(`/sharing/${token}/${data.data?.waivers[0].id}`);
        } else if (waiver_id) {
          setItem(
            data.data?.waivers?.find(w => String(w.id) === String(waiver_id))
          );
        }
        setSharing(data.data);
      }
    } finally {
      setLoading(false);
    }
  }

  /*async function loadWaivers() {
    const { success, data, error } = await loadWaiversByToken(token);
    if (success) {
      if (data.data?.length === 1) {
      }
      setItems(data.data);
    }
  }*/

  function getFormData() {
    let formData: { [key: string]: any } = {};
    item?.fields.forEach(block => {
      let formBlock: { [key: string]: any } = {};
      let blockHasField = false;
      block.rows?.forEach(row => {
        let formRow: { [key: string]: any } = {};
        let rowHasField = false;
        row.fields?.forEach(field => {
          if (!field.readonly) {
            blockHasField = true;
            rowHasField = true;
            formRow[field.id] = field.value;
          }
        });
        if (rowHasField) {
          formBlock[row.id] = formRow;
        }
      });
      if (blockHasField) {
        formData[block.id] = formBlock;
      }
    });
    return formData;
  }

  function validate() {
    let _errors: any[] = [];
    let errorCount = 0;

    function isValueNull(value: any) {
      return value === undefined || value === null || value === "";
    }

    item?.fields.forEach((block, blockIndex) => {
      let blockErrors: any[] = [];
      block.rows?.forEach((row, rowIndex) => {
        let rowErrors: any[] = [];
        row.fields?.forEach((field, fieldIndex) => {
          if (!field.readonly && field.required) {
            switch (field.type) {
              case FieldType.signature:
                if (
                  isValueNull(field.value) ||
                  isValueNull(field.value.signature) ||
                  isValueNull(field.value.name)
                ) {
                  errorCount++;
                  rowErrors.push(`${field.name} cannot be blank.`);
                } else {
                  rowErrors.push(null);
                }
                break;
              default:
                if (
                  isValueNull(field.value) ||
                  (Array.isArray(field.value) && field.value.length === 0)
                ) {
                  errorCount++;
                  rowErrors.push(`${field.name} cannot be blank.`);
                } else {
                  rowErrors.push(null);
                }
                break;
            }
          }
        });
        blockErrors.push(rowErrors);
      });
      _errors.push(blockErrors);
    });

    setErrors(_errors);
    return errorCount <= 0;
  }

  async function submit() {
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const formData = getFormData();
      let submitData = { data: formData, waiver_id: item?.id };

      const { success, data, error } = await submitSharingWaiver(
        token,
        submitData
      );
      if (success) {
        setShowSuccessModal(true);
      } else if (error?.form_errors?.fields && error?.form_errors?.fields[0]) {
        setErrors(error.form_errors.fields[0]);
      } else if (error?.form_errors?.waiver_id == "Limit reached.") {
        globalMessageHandler.error("Limit reached.");
      } else {
        globalMessageHandler.error(I18n.t("App.errors.Unknown error happend"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleFillAnother() {
    setShowSuccessModal(false);
    setErrors(null);

    if (hasMoreWaivers) {
      // Navigate back to waiver selection list
      navigate(`/sharing/${token}`);
    } else {
      // Generate new form key to force UI rerender
      setFormKey(`form-${Date.now()}`);

      // Clear errors and reset pages
      setErrors(null);
      setPages([]);

      // Reset current form for refill
      setItem(prevItem => {
        if (!prevItem) return prevItem;

        // Deep clone and reset form values
        const resetItem = JSON.parse(JSON.stringify(prevItem));
        resetItem.fields.forEach((block: any) => {
          block.rows?.forEach((row: any) => {
            row.fields?.forEach((field: any) => {
              if (!field.readonly) {
                field.value =
                  field.type === "signature" ? { signature: "", name: "" } : "";
              }
            });
          });
        });

        return resetItem;
      });

      // If it's a PDF form, reload the PDF pages
      if (item?.type === "file" && item?.file?.url) {
        // The PDF will be reloaded automatically when the component re-renders
        // due to the new formKey
      }
    }
  }

  const themeChanged = useCallback((event: any) => {
    if (event.detail) {
      dispatch(setThemeMode(event.detail));
    }
  }, []);

  useEffect(() => {
    if (!previewItem) {
      loadSharing();
    }
  }, [token]);

  useEffect(() => {
    if (!previewItem && sharing) {
      setItem(sharing?.waivers?.find(w => String(w.id) === String(waiver_id)));
    }
  }, [waiver_id, sharing]);

  useEffect(() => {
    document.addEventListener("themeChanged", themeChanged);
    return () => document.removeEventListener("themeChanged", themeChanged);
  }, []);

  return {
    token,
    waiver_id,
    sharing,
    setSharing,
    item,
    setItem,
    hasMoreWaivers,
    pages,
    setPages,
    handleLoadSuccess,
    handleLoadError,
    submit,
    previewItem,
    errors,
    setErrors,
    showSuccessModal,
    setShowSuccessModal,
    handleFillAnother,
    formKey,
    loading,
    submitting,
  };
}
